import { createWithEqualityFn } from 'zustand/traditional';
import { persistForUser } from '@erikdakoda/ui/shared/persistForUser';
import type { StoreApi } from 'zustand';
import type { UseBoundStoreWithEqualityFn } from 'zustand/traditional';

type LabelSelectionState = {
  /** Id of the selected section (folder), or `null` when none is selected. */
  selectedSectionId: string | null;
};

type LabelSelectionAction = {
  setSelectedSectionId: (sectionId: string | null) => void;
};

export type LabelSelectionStore = LabelSelectionState & LabelSelectionAction;

export type LabelSelectionStoreHook = UseBoundStoreWithEqualityFn<StoreApi<LabelSelectionStore>>;

const stores = new Map<string, LabelSelectionStoreHook>();

function createLabelSelectionStore(listId: string): LabelSelectionStoreHook {
  return createWithEqualityFn<LabelSelectionStore>()(
    persistForUser(
      (set) => ({
        selectedSectionId: null,

        setSelectedSectionId: (selectedSectionId) => set({ selectedSectionId }),
      }),
      {
        name: `label-selection-${listId}`,
        partialize: (state) => ({ selectedSectionId: state.selectedSectionId }),
      },
    ),
  );
}

/**
 * Returns the persisted selection store for a sidebar list, creating it on first use.
 * One localStorage key (`label-selection-&lt;listId>`) holds the selected section id.
 * @param listId Sidebar list id (`bots`, `prompts`, `metrics`, …).
 * @returns The zustand store hook for that list.
 */
export function getLabelSelectionStore(listId: string): LabelSelectionStoreHook {
  const existing = stores.get(listId);
  if (existing) {
    return existing;
  }
  const store = createLabelSelectionStore(listId);
  stores.set(listId, store);
  return store;
}

export type LabelSelectionApi = {
  selectedSectionId: string | null;
  setSelectedSectionId: (sectionId: string | null) => void;
};

/**
 * Subscribes a component to a list's selected folder (section).
 * @param listId Sidebar list id; must be stable for the lifetime of the component.
 * @returns The selected section id and the setter.
 */
export function useLabelSelection(listId: string): LabelSelectionApi {
  const useStore = getLabelSelectionStore(listId);
  const selectedSectionId = useStore((state) => state.selectedSectionId);
  const setSelectedSectionId = useStore((state) => state.setSelectedSectionId);
  return { selectedSectionId, setSelectedSectionId };
}
